import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Plus, Trash2, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Parse a proxyAddresses list into { primary, aliases[] }. Uppercase SMTP: = primary.
export function parseProxies(list) {
  let primary = null;
  const aliases = [];
  for (const raw of list || []) {
    const p = (raw || "").trim();
    if (!p) continue;
    const idx = p.indexOf(":");
    const scheme = idx >= 0 ? p.slice(0, idx) : "smtp";
    const addr = idx >= 0 ? p.slice(idx + 1) : p;
    if (scheme === "SMTP" && !primary) primary = addr;
    else if (scheme.toLowerCase() === "smtp") aliases.push(addr);
  }
  return { primary, aliases };
}

// Rebuild canonical proxyAddresses from primary + aliases.
export function buildProxies(primary, aliases) {
  const out = [`SMTP:${primary}`];
  const seen = new Set([primary.toLowerCase()]);
  for (const a of aliases || []) {
    const v = (a || "").trim();
    if (v && !seen.has(v.toLowerCase())) { out.push(`smtp:${v}`); seen.add(v.toLowerCase()); }
  }
  return out;
}

export function EmailAliasManager({ value, onChange, domains, disabled, excludeId, t }) {
  const { primary, aliases } = parseProxies(value);
  const all = primary ? [primary, ...aliases] : aliases;
  const [local, setLocal] = useState("");
  const [dom, setDom] = useState((domains[0] || {}).id || "");
  const [adding, setAdding] = useState(false);

  const emit = (newPrimary, newAliases) => onChange(buildProxies(newPrimary, newAliases));

  const setDefault = (addr) => {
    if (addr === primary) return;
    const rest = all.filter((a) => a !== addr);
    emit(addr, rest);
  };

  const remove = (addr) => {
    if (all.length <= 1) { toast.error(t("det.emailMinOne")); return; }
    if (addr === primary) {
      const rest = aliases;
      emit(rest[0], rest.slice(1));
    } else {
      emit(primary, aliases.filter((a) => a !== addr));
    }
  };

  const add = async () => {
    const email = `${local.trim()}@${dom}`;
    if (!local.trim() || !dom) { toast.error(t("onb.emailInvalid")); return; }
    if (all.some((a) => a.toLowerCase() === email.toLowerCase())) { toast.error(t("onb.emailDup")); return; }
    setAdding(true);
    try {
      const { data } = await api.get(`/validate/email?email=${encodeURIComponent(email)}${excludeId ? `&excludeId=${excludeId}` : ""}`);
      if (!data.available) {
        toast.error(data.reason === "domain" ? t("onb.emailDomain") : data.reason === "duplicate" ? t("onb.emailDup") : t("onb.emailInvalid"));
        return;
      }
      if (!primary) emit(email, []);
      else emit(primary, [...aliases, email]);
      setLocal("");
    } catch (e) { toast.error(t("onb.emailInvalid")); } finally { setAdding(false); }
  };

  return (
    <div className="space-y-2 md:col-span-2 lg:col-span-3" data-testid="email-alias-manager">
      <Label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500"><Mail size={13} /> {t("det.emailAliases")}</Label>
      <p className="text-[11px] text-zinc-400">{t("det.emailAliasesHint")}</p>
      <div className="divide-y divide-zinc-100 rounded-sm border border-zinc-200">
        {all.length === 0 && <div className="px-3 py-2 text-xs text-zinc-400">{t("det.emailNone")}</div>}
        {all.map((addr) => {
          const isPrimary = addr === primary;
          return (
            <div key={addr} className="flex items-center gap-2 px-3 py-2" data-testid={`email-row-${addr}`}>
              <button type="button" disabled={disabled} onClick={() => setDefault(addr)} title={t("det.emailSetDefault")}
                data-testid={`email-setdefault-${addr}`}
                className={`shrink-0 ${isPrimary ? "text-amber-500" : "text-zinc-300 hover:text-amber-400"} disabled:opacity-40`}>
                <Star size={15} fill={isPrimary ? "currentColor" : "none"} />
              </button>
              <span className="min-w-0 flex-1 truncate font-mono text-sm text-zinc-800">{addr}</span>
              {isPrimary
                ? <span className="shrink-0 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-amber-700" data-testid="email-primary-badge">SMTP</span>
                : <span className="shrink-0 rounded-sm bg-zinc-100 px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-zinc-500">smtp</span>}
              <button type="button" disabled={disabled || all.length <= 1} onClick={() => remove(addr)} title={t("common.remove") || "Remove"}
                data-testid={`email-remove-${addr}`} className="shrink-0 text-zinc-400 hover:text-red-500 disabled:opacity-30">
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
      </div>
      {!disabled && (
        <div className="flex items-stretch gap-2">
          <Input value={local} onChange={(e) => setLocal(e.target.value)} placeholder={t("det.emailAddPlaceholder")} data-testid="email-add-local"
            className="flex-1 rounded-sm border-zinc-300 font-mono text-sm" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
          <span className="flex items-center px-1 font-mono text-zinc-400">@</span>
          <Select value={dom} onValueChange={setDom}>
            <SelectTrigger className="w-56 rounded-sm border-zinc-300 font-mono text-sm" data-testid="email-add-domain"><SelectValue /></SelectTrigger>
            <SelectContent>{domains.map((d) => <SelectItem key={d.id} value={d.id}>{d.id}{d.isDefault ? " ·" : ""}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={add} disabled={adding} data-testid="email-add-btn" className="shrink-0 rounded-sm border-zinc-300">
            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          </Button>
        </div>
      )}
    </div>
  );
}
